const React = require('react')
const { useState, useEffect } = require('react')
const { useParams, useHistory } = require('react-router-dom')
const { Button, CardHeader, CardBody, Collapse } = require('reactstrap')
const { useNetworkConfig } = require('@contexts')
const { CopyButton } = require('../../copyButton')

const { createPublicClient, createWalletClient, http, custom } = require('viem')

const ExpandableWrite = props => {
    const { data, abi, account, index, func, isExpanded, onToggle } = props
    const history = useHistory()
    const { address } = useParams()
    const { network } = useNetworkConfig()

    const [inputValues, setInputValues] = useState({})
    const [writeResult, setWriteResult] = useState(null)
    const [isError, setIsError] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const [inputErrors, setInputErrors] = useState({})

    const validateInputs = () => {
        const errors = {}
        let isValid = true

        func.inputs.forEach((input, i) => {
            if (input.type !== 'string' && !inputValues[i]) {
                errors[i] = 'This field is required'
                isValid = false
            }
        })

        setInputErrors(errors)
        return isValid
    }

    const handleHeaderClick = e => {
        const isCopyButtonClick = e.target.closest('[data-copy-button]') !== null
        if (!isCopyButtonClick) {
            onToggle()
        }
    }

    const handleInputChange = (inputIndex, value) => {
        setInputValues(prev => ({
            ...prev,
            [inputIndex]: value
        }))
    }

    const handleWrite = async () => {
        if (!validateInputs()) return
        setIsLoading(true)
        setIsError(false)
        setWriteResult(null)
        try {
            const args = func.inputs.map((_, i) => inputValues[i] || '')
            const viem = createPublicClient({
                transport: http(network.data.endpoint)
            })
            const wallet = createWalletClient({
                chain: {
                    id: parseInt(network.data.chainId),
                    name: network.name,
                    nativeCurrency: {
                        name: network.token,
                        symbol: network.token,
                        decimals: 18
                    },
                    rpcUrls: [network.data.endpoint],
                    blockExplorerUrls: ['https://explorer.vbchain.vn']
                },
                transport: custom(window.ethereum)
            })

            const { request } = await viem.simulateContract({
                address: address,
                abi: data.information.contract.abi,
                functionName: func.name,
                args: args,
                account: account
            })

            const hash = await wallet.writeContract(request)
            const receipt = await viem.waitForTransactionReceipt({ hash })

            setWriteResult(receipt.transactionHash)
        } catch (error) {
            setIsError(true)
            if (error.message.includes('User rejected the request')) {
                setWriteResult('Metamask Tx Signature: User denied transaction signature.')
            } else {
                setWriteResult(`${error.message.replace('Version: viem@2.28.0', '').trim()}`)
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div style={{ marginBottom: '10px' }}>
            <CardHeader
                onClick={handleHeaderClick}
                style={{
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                    borderBottomLeftRadius: isExpanded ? '0px' : '4px',
                    borderBottomRightRadius: isExpanded ? '0px' : '4px',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    backgroundColor: 'var(--dropdown-background)',
                    color: 'var(--white-color)'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <span className={`${network.name}-text-color-after`} style={{ fontWeight: 'normal' }}>
                        {index + 1}. {func.name} ({func.signature})
                    </span>
                    <span>
                        <span data-copy-button='true'>
                            <CopyButton
                                id={`copy-function-name-${func.name}`}
                                data={func.name}
                                text={'Copy Method Name'}
                            />
                        </span>
                        <i
                            className={`fas fa-arrow-${isExpanded ? 'down' : 'right'}`}
                            style={{ marginLeft: '8px', color: 'var(--grey-color)' }}
                        />
                    </span>
                </div>
            </CardHeader>
            <Collapse isOpen={isExpanded}>
                <CardBody
                    style={{
                        padding: '15px',
                        backgroundColor: '#1e1e1e',
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px'
                    }}
                >
                    <div>
                        {func.inputs.map((input, i) => (
                            <div key={i} style={{ marginBottom: '12px' }}>
                                <label style={{ marginBottom: '5px', fontSize: '13px', color: 'var(--white-color)' }}>
                                    {input.name} ({input.type})
                                </label>
                                <input
                                    type='text'
                                    value={inputValues[i] || ''}
                                    onChange={e => handleInputChange(i, e.target.value)}
                                    placeholder={`${input.name} (${input.type})`}
                                    style={{
                                        width: '100%',
                                        padding: '8px 10px',
                                        background: 'var(--dropdown-background)',
                                        border: `1px solid ${inputErrors[i] ? 'red' : 'var(--grey-color)'}`,
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        color: 'var(--white-color)'
                                    }}
                                />
                                {inputErrors[i] && (
                                    <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                                        {inputErrors[i]}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div style={{ display: 'flex', alignItems: 'center', margin: '10px 0px' }}>
                            <Button
                                onClick={handleWrite}
                                disabled={isLoading || !account}
                                style={{
                                    padding: '4px 12px',
                                    background: !account ? 'var(--fail-color)' : 'var(--success-color)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: !account || isLoading ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    lineHeight: '1.5'
                                }}
                            >
                                {isLoading ? 'Writing...' : 'Write'}
                            </Button>
                            &nbsp;
                            {isError && (
                                <div
                                    style={{
                                        wordBreak: 'break-all',
                                        color: 'var(--fail-color)',
                                        fontSize: '13px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {writeResult}
                                </div>
                            )}
                            {writeResult && !isError && (
                                <Button
                                    onClick={() => history.push(`/${network.name}/tx/${writeResult}`)}
                                    style={{
                                        padding: '4px 12px',
                                        background: 'var(--success-color)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        lineHeight: '1.5'
                                    }}
                                >
                                    View your transaction
                                </Button>
                            )}
                        </div>
                    </div>
                </CardBody>
            </Collapse>
        </div>
    )
}

module.exports = { ExpandableWrite }
